// Slide-over panel for the AI usage badge. Opens from the right, shows the
// current month's credits remaining, per-agent breakdown, and a "Your
// usage / Org total" toggle. KAN-1293. (Daily safety brake removed in
// migration 074 — monthly cap is the only AI quota dimension.)
//
// Display contract (matches AIUsageBadge): 1 credit = 10 cents. The page
// shows credits, never dollars. Backend stays in cents — conversion is in
// the render layer via centsToCredits().

import * as React from "react";
import { Sparkles, X, Crown, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAIUsage } from "@/hooks/use-ai-usage";
import { useMyPendingPlanChangeRequest } from "@/hooks/use-plan-change-requests";
import { PlanChangeRequestModal } from "@/components/PlanChangeRequestModal";
import { useAuth } from "@/lib/auth";
import { PLAN_REQUEST_ROLES, type PlanTier } from "@/lib/plan-tier";
import { cn } from "@/lib/utils";
import { centsToCredits, remainingCredits } from "@/lib/credits";
import { aiUsageTestIds } from "@/testids/aiUsageTestIds";

function fmtCredits(n: number): string {
  return n.toLocaleString();
}
function pct(used: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}
function daysUntil(iso: string): string {
  const days = Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days}d`;
}
function tierLabel(t: string): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Upgrade CTA. Owns the pending-request query so it is only issued for roles
 *  that are allowed to read it — GET /plan-change-requests/me is gated too. */
function UpgradeCTA({ onUpgrade }: { onUpgrade: () => void }): React.ReactElement {
  const { data: pendingRequest } = useMyPendingPlanChangeRequest();

  if (pendingRequest) {
    return (
      <span
        className="text-xs font-medium text-muted-foreground"
        data-testid={aiUsageTestIds.upgradePendingBanner}
      >
        {pendingRequest.requestedPlan} requested - pending review
      </span>
    );
  }

  return (
    <Button
      size="sm"
      className="h-6 gap-1 text-[12px]"
      onClick={onUpgrade}
      data-testid={aiUsageTestIds.upgradeButton}
    >
      <Crown className="h-3 w-3" />
      Upgrade
    </Button>
  );
}

interface Props {
  onClose: () => void;
  /** Hide the Upgrade CTA in the embed widget — that lives with the SaaS owner, not their customers. */
  showUpgradeCTA?: boolean;
}

export function AIUsageSlideOver({ onClose, showUpgradeCTA = true }: Props): React.ReactElement {
  const [scope, setScope] = React.useState<"mine" | "org">("org");
  // Re-fetch per-scope. `fast: true` polls at 5s while the slide-over is
  // open; the topbar badge pauses its own timer meanwhile (each observer owns
  // its own refetch timer in React Query v5, so only one should poll a key)
  // and re-renders from the shared org-scope cache this query keeps warm.
  const { data, isLoading, isError } = useAIUsage(scope, { fast: true });
  const { userContext } = useAuth();
  const canRequestPlanChange = PLAN_REQUEST_ROLES.includes(userContext?.role ?? "end_user");
  const [showPlanModal, setShowPlanModal] = React.useState(false);

  const unlimited = (data?.limitCents ?? -1) < 0;
  const total = scope === "mine" ? (data?.mineCents ?? 0) : (data?.usedCents ?? 0);
  const limit = data?.limitCents ?? 0;
  const p = unlimited ? 0 : pct(total, limit);
  const danger  = !unlimited && p >= 90;
  const warning = !unlimited && p >= 75 && p < 90;
  const maxAgentSpend = Math.max(...(data?.byAgent.map((a) => a.cents) ?? [1]), 1);

  // ESC closes the slide-over.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !showPlanModal) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, showPlanModal]);

  return (
    <>
      <div
        className="fixed inset-0 z-[1000] bg-black/40"
        onClick={onClose}
        data-testid={aiUsageTestIds.overlay}
      />
      <div
        className="fixed right-0 top-0 z-[1001] h-full w-full max-w-md overflow-y-auto border-l border-border bg-card shadow-2xl" // design-tokens-ignore: overlay exception
        data-testid={aiUsageTestIds.panel}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-foreground" />
            <h2 className="text-base font-semibold text-foreground">AI credits</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close"
            data-testid={aiUsageTestIds.closeButton}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {isError && (
          <div className="m-5 rounded border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
            Failed to load AI usage. Try again in a moment.
          </div>
        )}

        {isLoading && (
          <div className="p-5 text-xs text-muted-foreground">Loading…</div>
        )}

        {data && !isError && (
          <div className="flex flex-col gap-5 p-5">
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  {tierLabel(data.tier)} plan · resets {daysUntil(data.resetAt)}
                </span>
                {showUpgradeCTA && canRequestPlanChange && data.tier !== "enterprise" && (
                  <UpgradeCTA onUpgrade={() => setShowPlanModal(true)} />
                )}
              </div>
              <div className="flex items-baseline gap-2">
                <span className={cn(
                  "text-2xl font-semibold tabular-nums",
                  danger  ? "text-destructive" :
                  warning ? "text-warning dark:text-warning" :
                            "text-foreground",
                )} data-testid={aiUsageTestIds.total}>
                  {unlimited ? fmtCredits(centsToCredits(total)) : fmtCredits(remainingCredits(total, data.limitCents))}
                </span>
                {!unlimited && (
                  <span className="text-sm text-muted-foreground tabular-nums">
                    of {fmtCredits(centsToCredits(data.limitCents))} credits left
                  </span>
                )}
                {unlimited && (
                  <span className="text-sm text-muted-foreground tabular-nums">
                    of <span className="text-base" aria-label="infinity">∞</span> credits used
                  </span>
                )}
              </div>
              {!unlimited && (
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      "h-full rounded-full transition-[background-color,border-color,color,opacity,transform]",
                      danger  ? "bg-destructive" :
                      warning ? "bg-warning" :
                                "bg-primary",
                    )}
                    style={{ width: `${p}%` }}
                    data-testid={aiUsageTestIds.progressFill}
                  />
                </div>
              )}
            </div>

            {/* Scope toggle */}
            <div className="flex gap-1 rounded-md border border-border bg-muted/30 p-0.5">
              {(["mine", "org"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  data-testid={aiUsageTestIds.scopeButton(s)}
                  className={cn(
                    "flex-1 rounded px-2 py-1 text-xs font-medium transition-colors",
                    scope === s
                      ? "bg-background text-foreground "
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {s === "mine" ? "Your usage" : "Org total"}
                </button>
              ))}
            </div>
            {scope === "mine" && data.usedCents > 0 && (
              <div className="-mt-3 text-[12px] text-muted-foreground">
                You used {Math.round((data.mineCents / Math.max(data.usedCents, 1)) * 100)}% of org credits this month.
              </div>
            )}

            {/* Per-agent breakdown */}
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-foreground">
                <TrendingUp className="h-3.5 w-3.5" />
                By agent
              </div>
              {data.byAgent.length === 0 ? (
                <div className="rounded border border-dashed border-border p-3 text-center text-[12px] text-muted-foreground">
                  No AI credits used yet this month.
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {data.byAgent.map((a) => {
                    const w = Math.max(2, Math.round((a.cents / maxAgentSpend) * 100));
                    return (
                      <div key={a.id} data-testid={aiUsageTestIds.agentRow(a.id)} className="flex flex-col gap-0.5">
                        <div className="flex items-center justify-between text-[12px]">
                          <span className="text-foreground">{a.label}</span>
                          <span className="text-muted-foreground tabular-nums">{fmtCredits(centsToCredits(a.cents))} credits</span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                          <div className="h-full rounded-full bg-foreground/60" style={{ width: `${w}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="rounded border border-border bg-muted/30 p-2.5 text-[12px] text-muted-foreground">
              Each model call uses credits — workflow builds, intelligence briefs, and the browser agent all draw from the same pool.
              Resets at the start of each calendar month (UTC).
            </div>
          </div>
        )}
      </div>

      {showPlanModal && (
        <PlanChangeRequestModal
          currentPlan={(data?.tier as PlanTier) || "free"}
          onClose={() => setShowPlanModal(false)}
        />
      )}
    </>
  );
}
